import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { DataManager, ODataV4Adaptor, Predicate, Query } from '@syncfusion/ej2-data';
import { AuthenticationService } from 'src/app/core/services/auth.service';
import { environment } from 'src/environments/environment';
import { ChatUIComponent, MessageModel, MessageSendEventArgs, MessageToolbarSettingsModel, UserModel } from '@syncfusion/ej2-angular-interactive-chat';

@Component({
  selector: 'app-comments',
  templateUrl: './comments.component.html',
  styleUrls: ['./comments.component.scss']
})
export class CommentsComponent implements OnInit, OnChanges {
  @Input() relatedConditions: { relatedTypeId: number; recordId: number }[] = [];
  @Output() closeScopeInfo = new EventEmitter<void>();

  @ViewChild('navTemplate', { static: true }) navTemplate: any;
  @ViewChild('chatUi') public chatUI: ChatUIComponent;

  title = 'Comments';
  data: DataManager;
  query: Query;
  baseUrl: string = environment.baseUrl.replace('/api/', '/odata/');

  currentUser: any;
  public chatMessage: MessageModel[] = [];
  public chatUser: UserModel = {};
  private loadSequence = 0;
  public messageToolbarSettings: MessageToolbarSettingsModel = {
    width: '50%',
    items: [
      { type: 'Button', iconCss: 'e-icons e-chat-copy', tooltip: 'Copy' },
      { type: 'Button', iconCss: 'e-icons e-chat-reply', tooltip: 'Reply' },
      { type: 'Button', iconCss: 'e-icons e-chat-pin', tooltip: 'Pin' },
    ]
  };

  constructor(private authenticationService: AuthenticationService) { }

  ngOnInit(): void {
    this.currentUser = this.authenticationService.currentUserValue;
    this.chatUser.id = this.currentUser.id;
    this.chatUser.user = `${this.currentUser.firstName} ${this.currentUser.lastName}`;

    this.data = new DataManager({
      url: this.baseUrl + 'Comments',
      insertUrl: this.baseUrl + 'Comments',
      adaptor: new ODataV4Adaptor(),
      headers: [{ Authorization: 'Bearer ' + this.currentUser.token }]
    });

    this.loadComments();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['relatedConditions'] && !changes['relatedConditions'].firstChange) {
      this.loadComments();
    }
  }

  private loadComments(): void {
    const sequence = ++this.loadSequence;
    if (!this.data || !this.relatedConditions?.length) {
      this.chatMessage = [];
      return;
    }

    const predicate = this.buildRelatedPredicate(this.relatedConditions);
    if (!predicate) {
      this.chatMessage = [];
      return;
    }

    this.query = new Query().expand('CreatedBy').where(predicate).sortBy('CreatedOn');

    this.data.executeQuery(this.query)
      .then((e: any) => {
        if (sequence !== this.loadSequence) {
          return;
        }
        const result = e.result ?? [];
        this.chatMessage = result
          .map((c: any) => ({
            id: String(c.Id),
            text: c.Comment,
            timeStamp: new Date(c.CreatedOn),
            author: {
              id: String(c.CreatedById),
              user: `${c.CreatedBy?.FirstName ?? ''} ${c.CreatedBy?.LastName ?? ''}`.trim()
            }
          }))
          .sort((a: MessageModel, b: MessageModel) => +a.timeStamp - +b.timeStamp);
      })
      .catch((e: any) => {
        console.error('Error fetching data:', e);
      });
  }

  private buildRelatedPredicate(conditions: { relatedTypeId: number; recordId: number }[]): Predicate | null {
    let finalPredicate: Predicate | null = null;
    for (const condition of conditions) {
      const predicate = new Predicate('RelatedTypeId', 'equal', condition.relatedTypeId)
        .and('RelatedId', 'equal', condition.recordId);
      finalPredicate = finalPredicate ? finalPredicate.or(predicate) : predicate;
    }
    return finalPredicate;
  }

  onMessageSend = async (args: MessageSendEventArgs) => {
    this.chatUI.suggestions = [];

    const tempId = `tmp_${Date.now()}`;
    const optimistic: MessageModel = {
      id: tempId,
      text: args.message.text!,
      timeStamp: new Date(),
      author: this.chatUser,
      status: { text: 'Sending...' }
    };
    this.chatMessage = [...this.chatMessage, optimistic];

    const dto = {
      Comment: args.message.text,
      RelatedTypeId: this.relatedConditions?.[0]?.relatedTypeId,
      RelatedId: this.relatedConditions?.[0]?.recordId,
      CreatedOn: new Date().toISOString(),
      CreatedById: this.currentUser.id
    };

    try {
      const created: any = await (this.data as any).insert(dto);
      const r = created?.result ?? created;

      const committed: MessageModel = {
        ...optimistic,
        id: String(r.Id ?? optimistic.id),
        text: r.Comment ?? optimistic.text,
        timeStamp: r.CreatedOn ? new Date(r.CreatedOn) : optimistic.timeStamp,
        status: { text: 'Sent', iconCss: 'e-icons e-check-double', tooltip: 'Delivered' }
      };
      this.chatMessage = this.chatMessage.map(m => m.id === tempId ? committed : m);
    } catch (err) {
      this.chatMessage = this.chatMessage.map(m =>
        m.id === tempId ? { ...m, status: { text: 'Failed', iconCss: 'e-icons e-warning', tooltip: 'Tap to retry' } } : m
      );
      console.error('Send failed', err);
    }
  }

  close() {
    this.closeScopeInfo.emit();
  }
}
